import { useSyncExternalStore } from 'react';

/**
 * The tour step that this tab chose while its `onboarding.setTourStep` call is in flight.
 *
 * Next and Back patch the cached `onboarding.state` at once. A realtime event can read the state
 * again before the server has the new step, and that answer holds the old step. Without this
 * store the guide bar moved back one stop. Rule: the local step wins while a call is in flight.
 * The store lives outside React because the bar and the welcome route each call `useTour`.
 */
let pendingStep: number | null = null;
let callsInFlight = 0;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function readPendingStep(): number | null {
  return pendingStep;
}

/** A step call starts: the bar shows `step` until the last call in flight ends. */
export function beginTourStepCall(step: number): void {
  pendingStep = step;
  callsInFlight += 1;
  notify();
}

/**
 * A step call ended. Returns the step to write to the cache when no other call is in flight,
 * else null (a later call still runs and owns the step).
 */
export function endTourStepCall(): number | null {
  callsInFlight = Math.max(0, callsInFlight - 1);
  if (callsInFlight > 0) {
    return null;
  }
  const settled = pendingStep;
  pendingStep = null;
  notify();
  return settled;
}

/** The tour ended: no local step is left. */
export function clearTourPendingStep(): void {
  pendingStep = null;
  callsInFlight = 0;
  notify();
}

/** The local step while a call is in flight, else null. */
export function useTourPendingStep(): number | null {
  return useSyncExternalStore(subscribe, readPendingStep, readPendingStep);
}
